import { getParamFromSearchOrHash } from '@shared/core';
import {
    clearRetailTokenFromPersistentStorage,
    getTargetSectionFromState,
    isCsrfFeatureEnabled,
    removeQueryStringParameter,
    setRetailToken,
} from '@retail/core';
import { setCookie } from '@luna/cookies';
import { createUnsafeLinkPath } from '@luna/router';
import { logger } from '@luna/logger';
import { isAlexProxyActive, isDevEnvironment, isFatEnvironment } from '@shared/environment';

import { getLogoutUrl } from './LogoutUrlService';

const PATHNAME_URL_PARAM = 'pathname';

function getCsrfToken(_state: string): string {
    let state = _state;

    if (typeof state === 'undefined' || state === '') {
        return '';
    }

    state = decodeURIComponent(state);
    const stateTokens = state.split('||');

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (stateTokens.length < 2) {
        return '';
    }

    return stateTokens[1];
}

function isCsrfTokenValid(csrfToken: string): boolean {
    const cookies = document.cookie.split('; ');
    return cookies.indexOf(`gt_csrf=${csrfToken}`) > -1;
}

function trackCsrfValidationError(accessToken: string): void {
    // simple fake tracking solution
    const request = new XMLHttpRequest();

    request.open('GET', 'csrf_tracking.html', false);
    if (accessToken) {
        request.setRequestHeader('Authorization', `bearer ${accessToken}`);
    }

    request.send(null);
}

function redirectToLogoutPage(state: string): void {
    clearRetailTokenFromPersistentStorage();

    document.location.href = getLogoutUrl('accessDenied', state);
}

function shouldCheckCSRFToken(): boolean {
    return (
        isCsrfFeatureEnabled() &&
        !isAlexProxyActive() &&
        !isFatEnvironment() &&
        !isDevEnvironment(window.location.hostname)
    );
}

function handleSearchState(): string {
    let { search } = window.location;

    // this holds all parameters that might appear in the hash
    const hashWithoutToken = window.location.hash
        .replace(/#access_token=[^$&]*&?/g, '')
        .replace(/&session_state=[^$&]*&?/g, '');

    if (search.length < 1) {
        search += `?${hashWithoutToken}`;
    } else {
        search += `&${hashWithoutToken}`;
    }

    return search;
}

export function handleRedirectionFromFedLoginOrSales(): void {
    let accessToken = getParamFromSearchOrHash('access_token') as string;

    const state = getParamFromSearchOrHash('state') as string;
    let targetSection = getTargetSectionFromState(state);

    if (shouldCheckCSRFToken()) {
        logger.debug('CSRF Feature Is Enabled');
        const csrfToken = getCsrfToken(state);

        logger.debug({ token: csrfToken });
        if (!isCsrfTokenValid(csrfToken)) {
            logger.info('CSRF Token is Invalid', { csrfToken, state });
            trackCsrfValidationError(accessToken);
            redirectToLogoutPage(createUnsafeLinkPath(targetSection));
            return;
        }
    }

    if (accessToken) {
        if (accessToken.indexOf('#') > -1) {
            // eslint-disable-next-line prefer-destructuring
            accessToken = accessToken.split('#')[0];
        }
        setRetailToken(accessToken);
    }

    const search = handleSearchState();

    // explicitly exclude these params from the URL
    const excludedParams = ['scope', 'expires_in', 'token_type', 'state'];

    let newUrl = `${window.location.origin}${window.location.pathname}${search}`;

    for (let j = 0; j < excludedParams.length; j += 1) {
        newUrl = removeQueryStringParameter(newUrl, excludedParams[j]);
    }

    if (targetSection.indexOf('handover/') > -1) {
        setCookie('handover_url', '1');
    }

    if (targetSection.length > 0) {
        if (targetSection.indexOf('handover/') > -1) {
            const frags = targetSection.split('/');
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            targetSection = frags.slice(2, frags.length).join('/');
        }

        if (targetSection) {
            newUrl = `${newUrl}${createUnsafeLinkPath(targetSection)}`;
        }
    }

    const targetUrl = new URL(newUrl);

    const pathnameOverride = targetUrl.searchParams.get(PATHNAME_URL_PARAM);
    if (pathnameOverride) {
        targetUrl.searchParams.delete(PATHNAME_URL_PARAM);
        targetUrl.pathname = pathnameOverride;
    }

    logger.info(targetUrl.toString());
    window.location.replace(targetUrl);
}
